import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

export function AnimatedEntrance() {
  // Use an animation state machine instead of just visible/not visible
  const [animationState, setAnimationState] = useState('initial');

  useEffect(() => {
    // Check if this is the first visit
    const hasVisited = localStorage.getItem('solomon_visited');

    if (hasVisited) {
      setAnimationState('hidden');
      return;
    }

    // Start the loading animation
    setAnimationState('visible');

    // Set a timeout to trigger fadeout
    const timer = setTimeout(() => {
      setAnimationState('fadeOut');

      // Save the visited state
      localStorage.setItem('solomon_visited', 'true');
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Hide completely when animation is done
  if (animationState === 'hidden') return null;

  // Animation variants for clearer sequencing
  const containerVariants = {
    initial: { opacity: 1 },
    visible: { opacity: 1 },
    fadeOut: { opacity: 0, transition: { duration: 0.5 } },
  };

  const contentVariants = {
    initial: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  const titleVariants = {
    initial: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.3, delay: 0.1 },
    },
  };

  const subtitleVariants = {
    initial: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0.25, duration: 0.3 },
    },
  };

  const loadingBackgroundVariants = {
    initial: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0.25, duration: 0.3 },
    },
  };

  const loadingBarVariants = {
    initial: { width: '0%' },
    visible: {
      width: '100%',
      transition: { duration: 1.2, ease: 'easeInOut' },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="initial"
      animate={animationState}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black"
      onAnimationComplete={(definition) => {
        if (definition === 'fadeOut') {
          setAnimationState('hidden');
        }
      }}>
      <motion.div
        className="relative px-2"
        variants={contentVariants}
        initial="initial"
        animate="visible">
        <motion.div
          className="mb-4 flex items-center justify-center font-logo text-6xl font-medium leading-relaxed text-white md:text-8xl"
          variants={titleVariants}
          initial="initial"
          animate="visible">
          Solomon
          <motion.span
            animate={{ color: ['#ffffff', '#ffffff'] }}
            transition={{ duration: 1.5, repeat: 0 }}
            className="text-white">
            Q
          </motion.span>
        </motion.div>

        <motion.div
          className="mb-12 flex justify-center"
          variants={subtitleVariants}
          initial="initial"
          animate="visible">
          <span className="text-xl tracking-wider text-white">
            SEMNALE FINANCIARE CU IMPACT
          </span>
        </motion.div>

        {/* Background visualization elements */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          {/* Grid pattern */}
          <motion.svg
            className="absolute h-full w-full opacity-20"
            viewBox="0 0 100 100"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            transition={{ duration: 1 }}>
            <defs>
              <pattern
                id="grid"
                width="10"
                height="10"
                patternUnits="userSpaceOnUse">
                <path
                  d="M 10 0 L 0 0 0 10"
                  fill="none"
                  stroke="white"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </motion.svg>

          {/* Animated circles representing data points */}
          <motion.div
            className="absolute left-1/4 top-1/4 h-2 w-2 rounded-full bg-blue-500"
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 2,
              times: [0, 0.5, 1],
              repeat: Infinity,
              repeatDelay: 0.5,
            }}
          />
          <motion.div
            className="absolute right-1/3 top-1/3 h-2 w-2 rounded-full bg-green-500"
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 2,
              times: [0, 0.5, 1],
              repeat: Infinity,
              repeatDelay: 0.3,
              delay: 0.3,
            }}
          />
          <motion.div
            className="absolute bottom-1/3 right-1/4 h-2 w-2 rounded-full bg-purple-500"
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 2,
              times: [0, 0.5, 1],
              repeat: Infinity,
              repeatDelay: 0.4,
              delay: 0.6,
            }}
          />

          {/* Stock chart line */}
          <motion.svg
            className="absolute bottom-1/4 left-0 h-32 w-full"
            viewBox="0 0 100 20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ duration: 1, delay: 0.8 }}>
            <motion.path
              d="M 0 15 Q 10 5, 20 10 T 40 5 T 60 10 T 80 5 T 100 10"
              fill="none"
              stroke="white"
              strokeWidth="0.5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, delay: 0.8, ease: 'easeInOut' }}
            />
          </motion.svg>
        </div>

        {/* Loading animation */}
        <motion.div
          className="mx-auto mt-4 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-neutral-800"
          variants={loadingBackgroundVariants}
          initial="initial"
          animate="visible">
          <motion.div
            className="h-full bg-white"
            variants={loadingBarVariants}
            initial="initial"
            animate="visible"
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
